import { useRef, useState } from "react";
import { User, Smartphone, AlertTriangle } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useAutoSavePatch } from "../../lib/useAutoSavePatch";
import SaveStatus from "./SaveStatus";
import ProfilePhotoUpload from "./ProfilePhotoUpload";

/**
 * Vermittler "Stammdaten" form.
 *
 * Phase 4.1 — User-Feedback:
 *  - Vorname / Nachname split (statt single "name")
 *  - Straße / Hausnummer split
 *  - „Ich bin Privatperson" Checkbox VOR der Steuer-ID (versteckt das Pflichtfeld)
 *  - Beziehungs-Dropdown: Freischaffend / Hobby / Zweiradhandel (+ optional Unternehmen)
 *  - AGB- und Datenschutz-Akzeptanz beim Speichern erforderlich
 */
export default function ProfileForm({ user, onSaved, onRelationshipChange, employerSlot }) {
    // Derive sensible defaults from legacy single-field values
    const splitName = () => {
        if (user.first_name || user.last_name) {
            return { first_name: user.first_name || "", last_name: user.last_name || "" };
        }
        const parts = (user.name || "").trim().split(/\s+/);
        if (parts.length >= 2) {
            return { first_name: parts.slice(0, -1).join(" "), last_name: parts.slice(-1)[0] };
        }
        return { first_name: parts[0] || "", last_name: "" };
    };
    const splitStreet = () => {
        if (user.street && user.house_number !== undefined && user.house_number !== null) {
            return { street: user.street, house_number: user.house_number };
        }
        // Legacy: street holds "Hauptstraße 12 a" — split last token-with-digit as Hausnr.
        const raw = (user.street || "").trim();
        const m = raw.match(/^(.*?)\s+(\d+\s*[a-zA-Z]?(?:[-/]\d+)?)\s*$/);
        if (m) return { street: m[1], house_number: m[2] };
        return { street: raw, house_number: user.house_number || "" };
    };

    const init = { ...splitName(), ...splitStreet() };

    const [form, setForm] = useState({
        first_name: init.first_name,
        last_name: init.last_name,
        street: init.street,
        house_number: init.house_number,
        postal_code: user.postal_code || "",
        city: user.city || "",
        country: user.country || "Deutschland",
        phone_mobile: user.phone_mobile || user.phone || "",
        contact_email: user.contact_email || user.email || "",
        private_email: user.private_email || user.contact_email || user.email || "",
        company_email: user.company_email || "",
        company_phone_ext: user.company_phone_ext || "",
        company_phone_mobile: user.company_phone_mobile || "",
        tax_id: user.tax_id || "",
        relationship_type: user.relationship_type || "",
        company_name: user.company_name || "",
        private_device_for_company: !!user.private_device_for_company,
    });
    const [terms, setTerms] = useState(!!user.terms_accepted_at);
    const [privacy, setPrivacy] = useState(!!user.privacy_accepted_at);
    const { save, saving, savedAt } = useAutoSavePatch("/users/me/vermittler-profile");
    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
    // Snapshot des zuletzt gespeicherten Zustands → erkennt Änderungen + ermöglicht „Rückgängig".
    const snapshotRef = useRef({
        first_name: init.first_name, last_name: init.last_name,
        street: init.street, house_number: init.house_number,
        postal_code: user.postal_code || "", city: user.city || "",
        country: user.country || "Deutschland",
        phone_mobile: user.phone_mobile || user.phone || "",
        contact_email: user.contact_email || user.email || "",
        private_email: user.private_email || user.contact_email || user.email || "",
        company_email: user.company_email || "",
        company_phone_ext: user.company_phone_ext || "",
        company_phone_mobile: user.company_phone_mobile || "",
        tax_id: user.tax_id || "", relationship_type: user.relationship_type || "",
        company_name: user.company_name || "",
        private_device_for_company: !!user.private_device_for_company,
    });

    const FIELD_LABELS = {
        first_name: "Vorname", last_name: "Nachname", street: "Straße", house_number: "Hausnummer",
        postal_code: "PLZ", city: "Stadt", phone_mobile: "Handynummer", contact_email: "E-Mail",
        private_email: "Privat-E-Mail", company_email: "Firmen-E-Mail",
        company_phone_ext: "Firmen-Durchwahl", company_phone_mobile: "Firmen-Handynummer",
        tax_id: "Steuer-ID", relationship_type: "Beziehung", company_name: "Unternehmen",
    };

    const buildCore = (override = {}) => {
        const f = { ...form, ...override };
        const requiresTaxId = f.relationship_type === "kleingewerbe" || f.relationship_type === "freischaffend";
        const payload = {
            ...f,
            // Legacy „name"-Feld konsistent halten für andere Stellen (Header, Listings)
            name: [f.first_name, f.last_name].filter(Boolean).join(" "),
            // is_private_person abgeleitet: privat wenn keine Steuer-ID-Pflicht
            is_private_person: !requiresTaxId,
        };
        if (!requiresTaxId) payload.tax_id = "";
        // Privat-E-Mail bleibt die primäre Kontakt-E-Mail (Legacy-Felder synchron halten).
        if (f.private_email) payload.contact_email = f.private_email.trim();
        if (f.relationship_type !== "zweiradhandel") {
            payload.company_name = "";
            payload.private_device_for_company = false;
        }
        return payload;
    };

    // Auto-Save: bei Feld-Verlassen (onBlur) bzw. Auswahl-Änderung. Speichert nur
    // bei echten Änderungen und bietet „Rückgängig".
    const persist = (override = {}) => {
        const next = { ...form, ...override };
        const prev = snapshotRef.current;
        const changed = Object.keys(snapshotRef.current).filter((k) => (next[k] ?? "") !== (prev[k] ?? ""));
        if (!changed.length) return;
        const undoPatch = {};
        changed.forEach((k) => { undoPatch[k] = prev[k] ?? ""; });
        snapshotRef.current = { ...snapshotRef.current, ...next };
        const labelField = FIELD_LABELS[changed[0]] || "Feld";
        save(buildCore(override), {
            undo: {
                label: `${labelField} geändert`,
                patch: undoPatch,
                apply: () => {
                    setForm((f) => ({ ...f, ...undoPatch }));
                    snapshotRef.current = { ...snapshotRef.current, ...undoPatch };
                },
            },
        });
    };

    // Consent separat: Timestamps nur setzen, wenn die jeweilige Box aktiv ist.
    const persistConsent = (nextTerms, nextPrivacy) => {
        const payload = buildCore();
        if (nextTerms) payload.terms_accepted_at = new Date().toISOString();
        if (nextPrivacy) payload.privacy_accepted_at = new Date().toISOString();
        save(payload);
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid="vermittler-profile-form">
            <div className="flex items-center gap-2 mb-1">
                <User className="w-5 h-5 text-[#FFCC00]" />
                <h2 className="font-display font-bold text-lg">Stammdaten</h2>
                <SaveStatus saving={saving} savedAt={savedAt} className="ml-auto" />
            </div>
            <p className="text-xs text-gray-600 mb-4">
                Persönliche Daten, Anschrift und Beziehung zum Zweiradhandel.
                Auszahlungs-Konto und Identitätsnachweis siehe unten.
            </p>

            <ProfilePhotoUpload user={user} onRefresh={onSaved} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Vorname *</Label>
                    <Input
                        value={form.first_name}
                        onChange={(e) => update("first_name", e.target.value)}
                        onBlur={() => persist()}
                        className="rounded-sm mt-1"
                        data-testid="v-first-name"
                        autoComplete="given-name"
                    />
                </div>
                <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Nachname *</Label>
                    <Input
                        value={form.last_name}
                        onChange={(e) => update("last_name", e.target.value)}
                        onBlur={() => persist()}
                        className="rounded-sm mt-1"
                        data-testid="v-last-name"
                        autoComplete="family-name"
                    />
                </div>
                <div className="md:col-span-2 grid grid-cols-[1fr_auto] gap-3">
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Straße</Label>
                        <Input
                            value={form.street}
                            onChange={(e) => update("street", e.target.value)}
                            onBlur={() => persist()}
                            className="rounded-sm mt-1"
                            data-testid="v-street"
                            autoComplete="address-line1"
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Hausnummer</Label>
                        <Input
                            value={form.house_number}
                            onChange={(e) => update("house_number", e.target.value)}
                            onBlur={() => persist()}
                            className="rounded-sm mt-1 w-28 font-mono-num"
                            data-testid="v-house-number"
                            autoComplete="address-line2"
                        />
                    </div>
                </div>
                <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">PLZ</Label>
                    <Input value={form.postal_code} onChange={(e) => update("postal_code", e.target.value)} onBlur={() => persist()} className="rounded-sm mt-1 font-mono-num" data-testid="v-postal" />
                </div>
                <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Stadt</Label>
                    <Input value={form.city} onChange={(e) => update("city", e.target.value)} onBlur={() => persist()} className="rounded-sm mt-1" data-testid="v-city" />
                </div>
                <div>
                    <div className="flex items-center justify-between gap-2">
                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Handynummer</Label>
                        {/* „Privates Gerät · €Punkte" — rechts neben Handynummer (User-Wunsch) */}
                        <label className="flex items-center gap-1.5 text-[11px] text-gray-700 cursor-pointer shrink-0" data-testid="v-private-device-wrap" title="Privates Gerät für Firmen-Apps genutzt — qualifiziert für €Punkte">
                            <input
                                type="checkbox"
                                checked={!!form.private_device_for_company}
                                onChange={(e) => { update("private_device_for_company", e.target.checked); persist({ private_device_for_company: e.target.checked }); }}
                                className="w-4 h-4 accent-[#FFCC00]"
                                data-testid="v-private-device"
                            />
                            <Smartphone className="w-3.5 h-3.5 text-gray-500" />
                            Privates Gerät · €Punkte
                        </label>
                    </div>
                    <Input value={form.phone_mobile} onChange={(e) => update("phone_mobile", e.target.value)} onBlur={() => persist()} className="rounded-sm mt-1 font-mono-num" data-testid="v-phone" />
                </div>
                <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Privat-E-Mail *</Label>
                    <Input type="email" value={form.private_email} onChange={(e) => update("private_email", e.target.value)} onBlur={() => persist()} placeholder="name@privat.de" className="rounded-sm mt-1" data-testid="v-private-email" />
                    <p className="text-[10px] text-gray-500 mt-1">Alles an diese Adresse gehört zu <strong>Privat</strong>.</p>
                    {!form.private_email && (
                        <div className="flex items-center gap-1.5 text-[11px] text-red-600 font-bold pt-1" data-testid="v-private-email-warning">
                            <AlertTriangle className="w-3.5 h-3.5" /> Pflichtfeld
                        </div>
                    )}
                </div>

                {/* Beziehung zum Zweiradhandel — Arbeitgeber-Card rechts daneben bei „Arbeite im Zweiradhandel" (User-Wunsch) */}
                <div className={"md:col-span-2 pt-1 grid grid-cols-1 gap-4 items-start " + (form.relationship_type === "zweiradhandel" ? "lg:grid-cols-2" : "")}>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Beziehung zum Zweiradhandel</Label>
                            <Select value={form.relationship_type || ""} onValueChange={(v) => { update("relationship_type", v); onRelationshipChange?.(v); persist({ relationship_type: v }); }}>
                                <SelectTrigger className="rounded-sm mt-1" data-testid="v-relationship-trigger">
                                    <SelectValue placeholder="– bitte wählen –" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="freischaffend" data-testid="v-rel-freischaffend">Freischaffend</SelectItem>
                                    <SelectItem value="hobby" data-testid="v-rel-hobby">Hobbymäßig</SelectItem>
                                    <SelectItem value="kleingewerbe" data-testid="v-rel-kleingewerbe">Kleingewerbe</SelectItem>
                                    <SelectItem value="zweiradhandel" data-testid="v-rel-zweiradhandel">Arbeite im Zweiradhandel</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {form.relationship_type === "zweiradhandel" && (
                            <div data-testid="v-company-wrap">
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Unternehmen (freiwillig)</Label>
                                <Input
                                    value={form.company_name}
                                    onChange={(e) => update("company_name", e.target.value)}
                                    onBlur={() => persist()}
                                    placeholder="z. B. Motorradzentrum Müller GmbH"
                                    className="rounded-sm mt-1"
                                    data-testid="v-company-name"
                                />
                            </div>
                        )}
                        {/* Firmen-E-Mail — Pflicht sobald im Zweiradhandel/mit Händler verknüpft (User-Wunsch) */}
                        {form.relationship_type === "zweiradhandel" && (
                            <div data-testid="v-company-email-wrap">
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Firmen-E-Mail *</Label>
                                <Input
                                    type="email"
                                    value={form.company_email}
                                    onChange={(e) => update("company_email", e.target.value)}
                                    onBlur={() => persist()}
                                    placeholder="name@firma.de"
                                    className="rounded-sm mt-1"
                                    data-testid="v-company-email"
                                />
                                <p className="text-[10px] text-gray-500 mt-1">Alles an diese Adresse / vom Firmenkonto gehört zur <strong>Firma</strong>.</p>
                                {!form.company_email && (
                                    <div className="flex items-center gap-1.5 text-[11px] text-red-600 font-bold pt-1" data-testid="v-company-email-warning">
                                        <AlertTriangle className="w-3.5 h-3.5" /> Pflichtfeld bei Händler-Verknüpfung
                                    </div>
                                )}
                                {/* Firmen-Telefon: Durchwahl (Pflicht) + Firmen-Handynummer (optional) */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                                    <div data-testid="v-company-phone-ext-wrap">
                                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Firmen-Durchwahl *</Label>
                                        <Input
                                            value={form.company_phone_ext}
                                            onChange={(e) => update("company_phone_ext", e.target.value)}
                                            onBlur={() => persist()}
                                            placeholder="z. B. -123"
                                            className="rounded-sm mt-1 font-mono-num"
                                            data-testid="v-company-phone-ext"
                                        />
                                        {!form.company_phone_ext && (
                                            <div className="flex items-center gap-1.5 text-[11px] text-red-600 font-bold pt-1" data-testid="v-company-phone-ext-warning">
                                                <AlertTriangle className="w-3.5 h-3.5" /> Pflichtfeld bei Händler-Verknüpfung
                                            </div>
                                        )}
                                    </div>
                                    <div data-testid="v-company-phone-mobile-wrap">
                                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Firmen-Handynummer (optional)</Label>
                                        <Input
                                            value={form.company_phone_mobile}
                                            onChange={(e) => update("company_phone_mobile", e.target.value)}
                                            onBlur={() => persist()}
                                            placeholder="z. B. +49 170 0000000"
                                            className="rounded-sm mt-1 font-mono-num"
                                            data-testid="v-company-phone-mobile"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* Steuer-ID — nur sichtbar wenn Kleingewerbe ODER Freischaffend */}
                        {(form.relationship_type === "kleingewerbe" || form.relationship_type === "freischaffend") && (
                            <div data-testid="v-tax-id-wrap">
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Steuer-ID *</Label>
                                <Input
                                    value={form.tax_id}
                                    onChange={(e) => update("tax_id", e.target.value)}
                                    onBlur={() => persist()}
                                    placeholder="DE 11 222 333 444"
                                    className="rounded-sm mt-1 font-mono-num"
                                    data-testid="v-tax-id"
                                />
                                <p className="text-[10px] text-gray-500 mt-1">
                                    11-stellige deutsche Steuer-Identifikationsnummer.
                                </p>
                            </div>
                        )}
                    </div>
                    {form.relationship_type === "zweiradhandel" && employerSlot && (
                        <div className="space-y-4" data-testid="stammdaten-employment-block">
                            {employerSlot}
                        </div>
                    )}
                </div>
            </div>

            {/* AGB + Datenschutz — Pflichtfelder (Auto-Save) */}
            <div className="mt-5 pt-4 border-t border-gray-200 space-y-2" data-testid="v-consent-block">
                <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={terms}
                        onChange={(e) => { setTerms(e.target.checked); persistConsent(e.target.checked, privacy); }}
                        className="mt-0.5 w-4 h-4 accent-[#FFCC00]"
                        data-testid="v-accept-terms"
                    />
                    <span className="text-xs text-gray-700">
                        Ich akzeptiere die {" "}
                        <a href="/agb" target="_blank" rel="noopener noreferrer" className="underline font-bold">AGB</a>. <span className="text-red-600 font-bold">*</span>
                    </span>
                </label>
                <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={privacy}
                        onChange={(e) => { setPrivacy(e.target.checked); persistConsent(terms, e.target.checked); }}
                        className="mt-0.5 w-4 h-4 accent-[#FFCC00]"
                        data-testid="v-accept-privacy"
                    />
                    <span className="text-xs text-gray-700">
                        Ich akzeptiere die {" "}
                        <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="underline font-bold">Datenschutzerklärung</a>. <span className="text-red-600 font-bold">*</span>
                    </span>
                </label>
                {(!terms || !privacy) && (
                    <div className="flex items-center gap-1.5 text-[11px] text-red-600 font-bold pt-1" data-testid="v-consent-warning">
                        <AlertTriangle className="w-3.5 h-3.5" /> Pflichtfelder — bitte beide akzeptieren.
                    </div>
                )}
            </div>
        </div>
    );
}
